import {
  Color,
  DoubleSide,
  Mesh,
  ShaderMaterial,
  SphereGeometry,
  Vector2,
  Vector3,
} from "three";
import { ScenesManager } from "../ScenesManager";

// interface SkyUniforms {
//   turbidity: { value: number };
//   rayleigh: { value: number };
//   mieCoefficient: { value: number };
//   mieDirectionalG: { value: number };
//   sunPosition: { value: Vector3 };
//   up: { value: Vector3 };
// }

export class Sky extends Mesh {
  material: ShaderMaterial;

  constructor() {
    super();
    const shader = Sky.SkyShader;

    const pixelRatio = ScenesManager.mainScene.renderer.getPixelRatio();

    this.material = new ShaderMaterial({
      name: "SkyShader",
      fragmentShader: shader.fragmentShader,
      vertexShader: shader.vertexShader,
      uniforms: {
        skyRadius: { value: 98 },
        env_c1: { value: new Color("#671c18") },
        env_c2: { value: new Color("#d48a7f") },
        noiseOffset: { value: new Vector3(100.01, 100.01, 100.01) },
        starSize: { value: 0.01 },
        starDensity: { value: 0.07 },
        clusterStrength: { value: 0.2 },
        clusterSize: { value: 0.5 },
        time: { value: 0 },
        noiseTexture: { value: ScenesManager.assets.fireSparkNoise },
        iResolution: { value: new Vector2(window.innerWidth * pixelRatio, window.innerHeight * pixelRatio) },
      },
      transparent: true,
      side: DoubleSide,
      depthWrite: false,
    });
    // console.log(window.innerWidth, window.innerHeight)
    // const material2 = new MeshStandardMaterial({ color: 0xff0000, side: BackSide });

    const geometry = new SphereGeometry(98, 32, 32);
    const mesh = new Mesh(geometry, this.material);
    this.add(mesh);
  }

  update(time: number) {
    // console.log("entrou")
    if (this.material) this.material.uniforms.time.value = time;
  }

  static SkyShader = {
    vertexShader: /*glsl*/ `
    varying vec3 vPos;
    varying vec2 vUv;

    void main() {
      vPos = position;
      vUv = uv;
      vec4 mvPosition = modelViewMatrix * vec4( vPos, 1.0 );
      gl_Position = projectionMatrix * mvPosition;
    }`,

    //FRAGMENT

    fragmentShader: /*glsl*/ `
    varying vec2 vUv;
    uniform vec3 iResolution;
    uniform float time;	
    uniform vec3 env_c1;
    uniform vec3 env_c2;
    varying vec3 vPos;
    uniform float skyRadius;

    vec4 permute(vec4 x){return mod(((x*34.0)+1.0)*x, 289.0);}
    vec4 taylorInvSqrt(vec4 r){return 1.79284291400159 - 0.85373472095314 * r;}
    vec3 fade(vec3 t) {return t*t*t*(t*(t*6.0-15.0)+10.0);}

    float cnoise(vec3 P){
      vec3 Pi0 = floor(P); // Integer part for indexing
      vec3 Pi1 = Pi0 + vec3(1.0); // Integer part + 1
      Pi0 = mod(Pi0, 289.0);
      Pi1 = mod(Pi1, 289.0);
      vec3 Pf0 = fract(P); // Fractional part for interpolation
      vec3 Pf1 = Pf0 - vec3(1.0); // Fractional part - 1.0
      vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
      vec4 iy = vec4(Pi0.yy, Pi1.yy);
      vec4 iz0 = Pi0.zzzz;
      vec4 iz1 = Pi1.zzzz;

      vec4 ixy = permute(permute(ix) + iy);
      vec4 ixy0 = permute(ixy + iz0);
      vec4 ixy1 = permute(ixy + iz1);

      vec4 gx0 = ixy0 / 7.0;
      vec4 gy0 = fract(floor(gx0) / 7.0) - 0.5;
      gx0 = fract(gx0);
      vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0);
      vec4 sz0 = step(gz0, vec4(0.0));
      gx0 -= sz0 * (step(0.0, gx0) - 0.5);
      gy0 -= sz0 * (step(0.0, gy0) - 0.5);

      vec4 gx1 = ixy1 / 7.0;
      vec4 gy1 = fract(floor(gx1) / 7.0) - 0.5;
      gx1 = fract(gx1);
      vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1);
      vec4 sz1 = step(gz1, vec4(0.0));
      gx1 -= sz1 * (step(0.0, gx1) - 0.5);
      gy1 -= sz1 * (step(0.0, gy1) - 0.5);

      vec3 g000 = vec3(gx0.x,gy0.x,gz0.x);
      vec3 g100 = vec3(gx0.y,gy0.y,gz0.y);
      vec3 g010 = vec3(gx0.z,gy0.z,gz0.z);
      vec3 g110 = vec3(gx0.w,gy0.w,gz0.w);
      vec3 g001 = vec3(gx1.x,gy1.x,gz1.x);
      vec3 g101 = vec3(gx1.y,gy1.y,gz1.y);
      vec3 g011 = vec3(gx1.z,gy1.z,gz1.z);
      vec3 g111 = vec3(gx1.w,gy1.w,gz1.w);

      vec4 norm0 = taylorInvSqrt(vec4(dot(g000, g000), dot(g010, g010), dot(g100, g100), dot(g110, g110)));
      g000 *= norm0.x;
      g010 *= norm0.y;
      g100 *= norm0.z;
      g110 *= norm0.w;
      vec4 norm1 = taylorInvSqrt(vec4(dot(g001, g001), dot(g011, g011), dot(g101, g101), dot(g111, g111)));
      g001 *= norm1.x;
      g011 *= norm1.y;
      g101 *= norm1.z;
      g111 *= norm1.w;

      float n000 = dot(g000, Pf0);
      float n100 = dot(g100, vec3(Pf1.x, Pf0.yz));
      float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z));
      float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
      float n001 = dot(g001, vec3(Pf0.xy, Pf1.z));
      float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z));
      float n011 = dot(g011, vec3(Pf0.x, Pf1.yz));
      float n111 = dot(g111, Pf1);

      vec3 fade_xyz = fade(Pf0);
      vec4 n_z = mix(vec4(n000, n100, n010, n110), vec4(n001, n101, n011, n111), fade_xyz.z);
      vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
      float n_xyz = mix(n_yz.x, n_yz.y, fade_xyz.x);
      return 2.2 * n_xyz;
    }

    float random (float x) {
        return fract(sin(x)*1e35);
    }

    vec2 random2( vec2 p ) {
        return fract(sin(vec2(dot(p,vec2(127.1,311.7)),dot(p,vec2(269.5,183.3))))*43758.5453);
    }

    float sdSegment(vec2 st, vec2 a, vec2 b )
    {
        vec2 pa = st-a, ba = b-a;
        float h = clamp( dot(pa,ba)/dot(ba,ba), 0.0, 0.25 );
        return length( pa - ba*h );
    }

    void mainImage( out vec4 fragColor, in vec2 fragCoord )
    {
        float cameraAngleX = atan(-cameraPosition.z, cameraPosition.x) + 3.14159265359;
        float cameraAngleY = (atan(cameraPosition.y) + 3.14159265359 / 2.0) * 2.0;
        vec2 st = (fragCoord.xy - iResolution.xy) / iResolution.y; 
        vec2 st2 = st ; 
        float freq = 1.1/skyRadius;
        float noise = cnoise(vPos * freq);
        vec4 backgroundColor = clamp(vec4(mix(env_c1, env_c2, noise*-1.), 1.0), 0.0, 1.0);

        
        float iTime = time * 0.15;
        //float iTime = time * 0.0;
        

        vec3 col = vec3(0.);    
        float coef = 6.;
        vec2 st0 = st;
        st *= coef;    
        st.y -= (iTime) * coef;
        st.x += sin(iTime * 1.0 * random(0.5));
        st.x += cos(cameraAngleX) * 2.0;
        st.y -= cameraAngleY / 3.14159265359;

        vec2 i_st = floor(st);
        vec2 f_st = fract(st);    
      
        float m_dist = 1.;

        for (int y= -1; y <= 1; y++) {
            for (int x= -1; x <= 1; x++) {
                
                vec2 neighbor = vec2(float(x) ,float(y));  
                
                vec2 point = random2(i_st + neighbor);            
                float sinTiming = 0.25 + 0.25*sin(6.2831);
                float cosTiming = 0.25 + 0.25*cos(6.2831);
                point.y = 1.0 + 0.5*sin(5. + 6.2831*point.y);
                point.x =0.5 + 0.3*sin(iTime * 5. + 6.2831 * point.x);

                vec2 diff = neighbor + point - f_st;

                float dist = sdSegment(diff, vec2(0.1), vec2(sinTiming * abs((point.x)), cosTiming * 0.1));
        
                m_dist = min(m_dist, dist);
            }
        }
        
        m_dist = 0.001 / m_dist;
        m_dist = abs(m_dist); 
        col += m_dist;
        st0 *= 0.5;   
        st0 = cos(st0);
        col *= vec3(1.5 * cos(st0.y ), 0.3, 0.1) * st0.x;       
        col = clamp(col, 0.0, 1.0); // Ensure col values are within [0.0, 1.0]

        col = mix(backgroundColor.rgb, col, 0.5);
        // fragColor = vec4(col,st2.y - 0.2);
        // fragColor = vec4(st2.y, 0.,0.,1.);
        fragColor = vec4(col,1.0);
        // fragColor = vec4(1.0);
    }


    void main() {
      mainImage(gl_FragColor, gl_FragCoord.xy);
    }
  `,
  };
}
